import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { EmptyState } from '@/components/primitives/EmptyState';
import { getPublicImages, getPublicTenants } from '@/api/public';
/**
 * Public image URL. NOT getImageUrl() — that points at /api/images/file,
 * which sits behind the Cognito authorizer, and this page is anonymous, so
 * every <img> 401'd and the gallery showed nothing. /api/public/* is
 * exempt from the authorizer and the route re-checks approval itself.
 */
const publicImageUrl = (filePath: string) =>
  `${import.meta.env.VITE_API_URL || '/api'}/public/images/file/${filePath}`;
import { cn } from '@/lib/utils';

const PAGE_SIZE = 24;

interface GalleryImage {
  id: number | string;
  file_path: string;
  description?: string | null;
  uploaded_at?: string | null;
  tenant_id?: number | null;
}

interface GalleryResponse {
  images: GalleryImage[];
  total: number;
  total_pages: number;
  page: number;
  page_size: number;
}

export default function GalleryPage() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GalleryImage | null>(null);
  // '' = every FPA. Only FPAs that actually have public photos are offered.
  const [tenantId, setTenantId] = useState<string>('');
  const [tenants, setTenants] = useState<Array<{ id: number; name: string }>>([]);

  const fetchImages = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { page: p, page_size: PAGE_SIZE };
      if (tenantId) params.tenant_id = Number(tenantId);
      const data = (await getPublicImages(params)) as GalleryResponse;
      setImages(data.images || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to load gallery:', err);
      setError('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchImages(page);
  }, [page, fetchImages]);

  // FPA options. Anonymous endpoint, so this works signed out.
  useEffect(() => {
    let cancelled = false;
    getPublicTenants()
      .then((data) => {
        if (!cancelled) setTenants((data as Array<{ id: number; name: string }>) || []);
      })
      .catch(() => {
        // Non-fatal — the filter just doesn't render.
      });
    return () => { cancelled = true; };
  }, []);

  const goToPage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <section className="relative isolate overflow-hidden bg-fire-700/90">
        <div className="absolute inset-0 -z-10 bg-[url('/images/hero-sunset-fire.jpg')] bg-cover bg-center opacity-40" />
        <div className="container relative px-4 py-10 text-center text-white sm:py-14">
          <h1 className="text-3xl font-bold tracking-tight drop-shadow sm:text-4xl">
            Photo gallery
          </h1>
          <p className="mt-2 text-white/85">
            Community-submitted wildfire photos
            {total > 0 ? ` — ${total} images` : ''}
          </p>
        </div>
      </section>

      <main className="container py-6">
        {tenants.length > 1 && (
          <div className="mb-4 flex items-center gap-2">
            <label htmlFor="gallery-fpa" className="text-sm text-muted-foreground">
              FPA area
            </label>
            <select
              id="gallery-fpa"
              value={tenantId}
              onChange={(e) => { setTenantId(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              <option value="">All FPAs</option>
              {tenants.map((t) => (
                <option key={t.id} value={String(t.id)}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] w-full rounded-md" />
            ))}
          </div>
        ) : images.length === 0 ? (
          <EmptyState title="No public images yet" description="Photos approved by moderators appear here." />
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {images.map((image) => (
              <button
                key={image.id}
                type="button"
                onClick={() => setSelected(image)}
                className="group relative overflow-hidden rounded-md border border-border bg-muted text-left transition hover:opacity-90"
              >
                <img
                  src={publicImageUrl(image.file_path)}
                  alt={image.description || `Wildfire image #${image.id}`}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
                {image.description && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-xs text-white">
                    <div className="line-clamp-2">{image.description}</div>
                    {image.uploaded_at && (
                      <div className="mt-0.5 text-white/70">
                        {new Date(image.uploaded_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    className={cn(page === 1 && 'pointer-events-none opacity-50')}
                    onClick={(e) => {
                      e.preventDefault();
                      if (page > 1) goToPage(page - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                    {page} / {totalPages}
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    className={cn(page === totalPages && 'pointer-events-none opacity-50')}
                    onClick={(e) => {
                      e.preventDefault();
                      if (page < totalPages) goToPage(page + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
          <DialogContent className="max-w-4xl">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="pr-8">
                    {selected.description || `Wildfire image #${selected.id}`}
                  </DialogTitle>
                </DialogHeader>
                <img
                  src={publicImageUrl(selected.file_path)}
                  alt={selected.description || `Wildfire image #${selected.id}`}
                  className="max-h-[70vh] w-full object-contain"
                />
                {selected.uploaded_at && (
                  <p className="text-xs text-muted-foreground">
                    Uploaded {new Date(selected.uploaded_at).toLocaleDateString()}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button variant="ghost" onClick={() => setSelected(null)}>
                    <X className="mr-2 h-4 w-4" /> Close
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </>
  );
}
